import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocationsState } from "../../../containers/LocationsStateContainer";
import { usePermissions } from "../../../hooks/usePermissions";
import { useThisHangar } from "../../../hooks/useThisHangar";
import { useHoldingArea } from "../../../hooks/useHoldingArea";
import { Hangar, Location, Ramp, Tenant } from "../../../types";
import { lastHangaredText } from "../../../utils/phrasing";
import { Permission } from "../../../utils/permissions";
import { LocationTextField } from "../../../widgets/LocationTextField";
import { HOLDING_AREA_ID } from "../../MultiHangar/HoldingArea";

type Props = {
  selectedTenant: Tenant;
  onAdd: (tenant: Tenant) => void;
  isReference: boolean;
  location?: Location<Hangar | Ramp>;
};

export const AddSelectedTenant: React.FC<Props> = ({
  selectedTenant,
  onAdd,
  isReference,
  location,
}) => {
  const { t } = useTranslation();
  const { findTenantLocation } = useLocationsState();
  const { hangar } = useThisHangar();
  const { entries: holdingAreaEntries } = useHoldingArea();
  const { hasPermission } = usePermissions();
  const canModifyTenants = hasPermission(Permission.MODIFY_TENANTS);

  const tenantLocation = findTenantLocation(isReference, selectedTenant);
  const isInThisHangar =
    location && tenantLocation?.location_id === location.location_id &&
    hangar?.stack?.tenants?.some((t) => t.id === selectedTenant.id);

  const currentTenantLocation = findTenantLocation(false, selectedTenant);

  // Check if tenant is in the holding area using the entries from useHoldingArea
  const isInHoldingArea = holdingAreaEntries.some((entry) => entry.tenant_id === selectedTenant.id);

  // For holding area: check if aircraft is in a different location (any hangar)
  const isHoldingArea = location?.id === HOLDING_AREA_ID;
  const isInDifferentLocation = currentTenantLocation && currentTenantLocation.id !== location?.id;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t("settings.hangarSettings.addSelectedTenant.table.headers.tailNumber")}</TableCell>
          <TableCell>{t("settings.hangarSettings.addSelectedTenant.table.headers.model")}</TableCell>
          <TableCell>{t("settings.hangarSettings.addSelectedTenant.table.headers.currentLocation")}</TableCell>
          <TableCell>{t("settings.hangarSettings.addSelectedTenant.table.headers.lastHangared")}</TableCell>
          <TableCell>{t("settings.hangarSettings.addSelectedTenant.table.headers.type")}</TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>{selectedTenant.tail_number}</TableCell>
          <TableCell>{selectedTenant.aircraft?.model}</TableCell>
          <TableCell>
            <LocationTextField
              selectedLocationId={currentTenantLocation?.location_id}
              readOnly
              unassignedText={t("settings.hangarSettings.addSelectedTenant.unassignedText")}
            />
          </TableCell>
          <TableCell>
            {lastHangaredText(
              selectedTenant.last_hangared,
              Boolean(currentTenantLocation)
            )}
          </TableCell>
          <TableCell>{selectedTenant.type}</TableCell>
          <TableCell>
            {isInHoldingArea ? (
              <Typography variant="caption">{t("settings.hangarSettings.addSelectedTenant.status.inHoldingArea")}</Typography>
            ) : isInThisHangar ? (
              <Typography variant="caption">{t("settings.hangarSettings.addSelectedTenant.status.inThisHangar")}</Typography>
            ) : isHoldingArea && isInDifferentLocation ? (
              <Typography variant="caption">
                {t("settings.hangarSettings.addSelectedTenant.status.inLocation", {
                  locationName: currentTenantLocation.name,
                })}
              </Typography>
            ) : (
              <Button
                variant="contained"
                color="success"
                disabled={!canModifyTenants}
                onClick={() => {
                  onAdd({
                    ...selectedTenant,
                  });
                }}
              >
                {t("settings.hangarSettings.addSelectedTenant.buttons.add")}
              </Button>
            )}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};
